import { useCallback, useRef, useEffect } from 'react';
import {
  axiosInt,
  API_TEMPLATES_URL,
  downloadBlob,
  searchJmesPath,
} from '../../../utils';
import renderContentUtils from '../../../components/TemplateEditor/utils/renderContent';
import {
  ensureThymeleafConditionWrappers,
  ensureDefaultDateFormats,
} from '../../../components/TemplateEditor/utils/shared/thymeleaf';
import { usePage } from '../..';
import type { Editor as TinyMCEEditorType } from 'tinymce';
import type { IFormEditorProps } from './index';
import { renderTemplate } from '../../../components/TemplateEditor/parsing';

const useInitEditor = ({
  componentProps,
  handleChange,
}: {
  componentProps: IFormEditorProps;
  handleChange: (value: string) => void;
}) => {
  const pageContext = usePage() ?? {};

  const propsRef = useRef(componentProps);
  const pageRef = useRef(pageContext);

  useEffect(() => {
    propsRef.current = componentProps;
  });
  useEffect(() => {
    pageRef.current = pageContext;
  });

  const handleTemplateInit = useCallback((editor: TinyMCEEditorType) => {
    editor.ui.registry.addMenuButton('loadFromTemplate', {
      text: 'Load from Template',
      onSetup: api => {
        if (!componentProps.templateEnabled) {
          api.setEnabled(false);
        }
        return () => {};
      },
      fetch: async callback => {
        const props = propsRef.current;
        if (!props.templateIds?.length) {
          callback([]);
          return;
        }

        editor.mode.set('readonly');
        try {
          const responses = await Promise.all(
            props.templateIds.map(id =>
              axiosInt.get(`${API_TEMPLATES_URL}/templates/${id}`)
            )
          );
          const systemVariables = (
            await axiosInt.get(`${API_TEMPLATES_URL}/system-variables`)
          ).data;

          callback(
            responses.map(res => ({
              type: 'menuitem' as const,
              text: res.data.name ?? res.data.id,
              onAction: () => {
                const pageData = pageRef.current;
                const content: string = res.data.content;
                let data: Record<string, unknown> = { ...pageData };
                const currentProps = propsRef.current;
                if (currentProps.templateDataMapping) {
                  const mapped = searchJmesPath(
                    data,
                    currentProps.templateDataMapping
                  );

                  if (
                    mapped &&
                    typeof mapped === 'object' &&
                    !Array.isArray(mapped)
                  ) {
                    data = mapped as Record<string, unknown>;
                  }
                }
                handleChange(
                  renderTemplate(content, { ...data, ...systemVariables })
                );
              },
            }))
          );
        } catch {
          callback([]);
        } finally {
          editor.mode.set('design');
        }
      },
    });
  }, []);

  const handleDownloadInit = useCallback((editor: TinyMCEEditorType) => {
    const handleDownload = async (contentType: 'html' | 'pdf') => {
      const content = editor.getContent();
      const processed = ensureDefaultDateFormats(
        ensureThymeleafConditionWrappers(content)
      );
      const pageData = pageRef.current;
      editor.mode.set('readonly');
      try {
        const response = await axiosInt.post(
          `${API_TEMPLATES_URL}/templates/render`,
          { content: processed, parameters: { '@': pageData } }, // TODO: add transformer
          { params: { contentType }, responseType: 'blob' }
        );
        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/:/g, '-');
        downloadBlob(response.data, `template_${timestamp}.${contentType}`);
      } catch {
        // silent fail
      } finally {
        editor.mode.set('design');
      }
    };

    editor.ui.registry.addMenuButton('exportFile', {
      text: 'Export',
      onSetup: api => {
        if (!componentProps.downloadEnabled) api.setEnabled(false);
        return () => {};
      },
      fetch: callback => {
        callback([
          // { // FOR NOW JUST PDF
          //   type: 'menuitem' as const,
          //   text: 'HTML',
          //   onAction: () => handleDownload('html'),
          // },
          {
            type: 'menuitem' as const,
            text: 'PDF',
            onAction: () => handleDownload('pdf'),
          },
        ]);
      },
    });
  }, []);

  const handleInit = useCallback((editor: TinyMCEEditorType) => {
    handleTemplateInit(editor);
    handleDownloadInit(editor);
  }, []);

  return handleInit;
};

export default useInitEditor;
